import React, { useCallback, useEffect, useRef, useState } from 'react';

import { saveAs } from 'file-saver';
import { MDBBtn, MDBCol, MDBContainer, MDBIcon, MDBRow, MDBSpinner } from 'mdbreact';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../../../../services/api';
import { getData, getPerfil } from '../../../../services/auth';
import resolverMensagemErro from '../../../../utils/resolverMensagemErro';
import preparaAnexos from './relatorio/anexos';
import ehCelular from './relatorio/dispositivo';
import nomeDoEmissor from './relatorio/emissor';
import gerarPdfAbordagem, { nomeDoArquivo } from './relatorio/gerarPdfAbordagem';

// getData() estoura se o token estiver ausente ou corrompido; o rodapé cai para
// o perfil salvo nesse caso, não deixa de sair.
const dadosDoToken = () => {
  try {
    return getData();
  } catch (erro) {
    return null;
  }
};

/**
 * Relatório da abordagem em PDF (issue #256).
 *
 * A tela não desenha mais o relatório em HTML: ela monta o PDF e o exibe num
 * iframe. Assim o que aparece na tela é exatamente o arquivo que será baixado
 * ou impresso — antes eram duas saídas diferentes, e só o navegador sabia
 * transformar uma na outra.
 */
export default function Imprimir(props) {
  const history = useHistory();
  const { abordagem_id: abordagemId } = props.match.params;

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [arquivo, setArquivo] = useState({ blob: null, url: null });
  const iframe = useRef(null);
  // no celular não há preview: o Chrome no Android não renderiza PDF em iframe
  const celular = useRef(ehCelular(typeof navigator === 'undefined' ? null : navigator));

  const monta = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const abordagem = await api.get(`/abordagem/${abordagemId}`);
      if (abordagem.data.erro) {
        toast.error(abordagem.data.erro);
        history.push('/app');
        return;
      }

      const [veiculos, abordados, empresas, ocorrencias, imagens] = await Promise.all([
        api.get(`/veiculos-abordagem/${abordagemId}`),
        api.get(`/abordados-abordagem/${abordagemId}`),
        api.get(`/empresas-abordagem/${abordagemId}`),
        api.get(`/inteligencia-abordagem/${abordagemId}`),
        api.get('/sgo-imagem', { params: { abordagem_id: abordagemId } }),
      ]);

      const blob = await gerarPdfAbordagem(
        {
          abordagem: abordagem.data,
          veiculos: veiculos.data,
          abordados: abordados.data,
          empresas: empresas.data,
          ocorrencias: ocorrencias.data,
          anexos: preparaAnexos(imagens.data),
        },
        { emitidoPor: nomeDoEmissor(dadosDoToken(), getPerfil()) }
      );

      setArquivo({ blob, url: URL.createObjectURL(blob) });

      // celular: entrega o arquivo assim que ele fica pronto, sem esperar toque
      if (celular.current) saveAs(blob, nomeDoArquivo(abordagemId));
    } catch (err) {
      const mensagem = resolverMensagemErro(err);
      setErro(mensagem);
      toast.error(mensagem);
    } finally {
      setCarregando(false);
    }
  }, [abordagemId, history]);

  useEffect(() => {
    monta();
  }, [monta]);

  // o objectURL segura o arquivo na memória do navegador até ser revogado
  useEffect(() => () => arquivo.url && URL.revokeObjectURL(arquivo.url), [arquivo.url]);

  const baixar = () => saveAs(arquivo.blob, nomeDoArquivo(abordagemId));

  const imprimir = () => {
    if (!iframe.current || !iframe.current.contentWindow) return;
    iframe.current.contentWindow.focus();
    iframe.current.contentWindow.print();
  };

  return (
    <MDBContainer className="py-3">
      <MDBRow between className="align-items-center mb-3">
        <MDBCol size="12" md="6">
          <h5 className="font-weight-bolder mb-0">Relatório da abordagem nº {abordagemId}</h5>
        </MDBCol>
        <MDBCol size="12" md="6" className="text-md-right mt-2 mt-md-0">
          <MDBBtn color="mdb-color" size="sm" onClick={() => history.goBack()}>
            <MDBIcon icon="arrow-left" className="mr-2" />
            Voltar
          </MDBBtn>
          {/* imprimir depende do iframe, que no celular não existe — lá quem
              imprime é o app que abre o PDF baixado */}
          {!celular.current && (
            <MDBBtn color="mdb-color" size="sm" disabled={!arquivo.blob} onClick={imprimir}>
              <MDBIcon icon="print" className="mr-2" />
              Imprimir
            </MDBBtn>
          )}
          {/* no celular a ação vive no cartão abaixo, que é o conteúdo da tela:
              repetida aqui em cima, viravam dois "Baixar PDF" na mesma dobra */}
          {!celular.current && (
            <MDBBtn color="success" size="sm" disabled={!arquivo.blob} onClick={baixar}>
              <MDBIcon icon="file-pdf" className="mr-2" />
              Baixar PDF
            </MDBBtn>
          )}
        </MDBCol>
      </MDBRow>

      {carregando && (
        <MDBRow center className="my-5 align-items-center">
          <MDBSpinner small />
          <span className="ml-2 font-weight-bolder">Gerando o relatório…</span>
        </MDBRow>
      )}

      {!carregando && erro && (
        <MDBRow center className="my-5">
          <MDBCol size="12" md="8" className="text-center">
            <p className="font-weight-bolder text-danger">{erro}</p>
            <MDBBtn color="mdb-color" size="sm" onClick={monta}>
              Tentar de novo
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      )}

      {!carregando && !erro && arquivo.url && !celular.current && (
        <iframe
          ref={iframe}
          title={`Relatório da abordagem ${abordagemId}`}
          src={arquivo.url}
          style={{ width: '100%', height: '80vh', border: '1px solid #d4dce2' }}
        />
      )}

      {!carregando && !erro && arquivo.url && celular.current && (
        <MDBRow center className="my-5">
          <MDBCol size="12" md="8" className="text-center">
            <MDBIcon icon="file-pdf" size="3x" className="mb-3" style={{ color: '#6b7c88' }} />
            <p className="font-weight-bolder mb-1">O download do relatório começou.</p>
            <p className="mb-3" style={{ color: '#6b7c88' }}>
              Procure <strong>{nomeDoArquivo(abordagemId)}</strong> nos seus downloads. Se nada
              baixou, toque no botão abaixo.
            </p>
            <MDBBtn color="success" size="sm" onClick={baixar}>
              <MDBIcon icon="file-pdf" className="mr-2" />
              Baixar PDF
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      )}
    </MDBContainer>
  );
}
